import React, { lazy } from 'react';
import { 
    CheckmarkIcon, 
    GoodieWhiteIcon, 
    SparklesIcon, 
    TrophyIcon, 
    CalendarIcon,
    StoreIcon
} from '../components/Icons';

export interface ModDefinition {
    id: string;
    name: string;
    icon: React.ComponentType<{ className?: string }> | string;
    component: React.LazyExoticComponent<React.ComponentType<any>>;
    route?: string;
    description: string;
    author?: string;
}

export const MOD_REGISTRY: Record<string, ModDefinition> = {
    'todo': {
        id: 'todo',
        name: 'Perfect To-Do',
        icon: '/assets/todo_icon.png',
        component: lazy(() => import('../components/TodoMod').then(m => ({ default: m.TodoMod }))),
        route: '/todo',
        description: 'A high-aesthetic task manager that keeps your digital life organized and modular.',
        author: 'agk'
    },
    'daily_footprint': {
        id: 'daily_footprint',
        name: 'Daily Footprint',
        icon: '/assets/footprint_logo.png',
        component: lazy(() => import('../components/FootprintPage').then(m => ({ default: m.FootprintPage }))),
        route: '/footprint',
        description: 'Track your daily impact, earn Life Points, and compete with your connections.',
        author: 'vibeman1'
    },
    'pimp_my_ride': {
        id: 'pimp_my_ride',
        name: 'Pimp My Ride',
        icon: '/assets/goodie_store_icon.png',
        component: lazy(() => import('../components/PimpMyRide').then(m => ({ default: m.PimpMyRide }))),
        route: '/pimp-my-ride',
        description: 'The premier boutique for modular experiences and customizations. Gifted to you by GoodieBoy.',
        author: 'system'
    },
    'art_class': {
        id: 'art_class',
        name: 'Art Class',
        icon: SparklesIcon,
        component: lazy(() => import('../components/ArtClass').then(m => ({ default: m.ArtClass }))),
        route: '/art-class',
        description: 'The ultimate creator studio. Design postcards, stickers, chat bubbles, and more to publish to the store.',
        author: 'vibeman1'
    }
};

export const getDynamicModDefinition = (mod: ModDefinition, usersData: any): ModDefinition => {
    if (!mod || !mod.author || !usersData[mod.author]) return mod;
    
    const authorData = usersData[mod.author];
    const customMod = (authorData.customMods || []).find((m: any) => m.id === mod.id);
    
    if (customMod && customMod.icon) {
        return {
            ...mod,
            name: customMod.name || mod.name,
            icon: customMod.icon
        };
    }
    
    return mod;
};

export const getActiveMods = (activeModIds: string[] = [], usersData: any = {}) => {
    return activeModIds
        .map(id => {
            if (MOD_REGISTRY[id]) {
                return getDynamicModDefinition(MOD_REGISTRY[id], usersData);
            }
            // Look for this mod in customMods of any user (typically the current user)
            for (const username of Object.keys(usersData)) {
                const customMod = (usersData[username]?.customMods || []).find((m: any) => m.id === id);
                if (customMod) {
                    return {
                        id: customMod.id,
                        name: customMod.name,
                        icon: customMod.icon || 'https://api.dicebear.com/7.x/bottts/svg?seed=mod',
                        description: customMod.url ? `Custom web app mod: ${customMod.url}` : 'Custom system mod generated by GoodieBoy.',
                        author: username,
                        url: customMod.url,
                        component: React.lazy(() => Promise.resolve({ default: () => null })) // dummy component for web mods
                    };
                }
            }
            return null;
        })
        .filter(mod => !!mod) as ModDefinition[];
};

export const getAllMods = (usersData: any = {}) => {
    return Object.values(MOD_REGISTRY).map(mod => getDynamicModDefinition(mod, usersData));
};
